import React, { useEffect, useContext } from 'react'
import { shape, string, func, number } from 'prop-types'

import { ReactReduxContext, useDispatch } from 'react-redux'

import env from '@config/env'
import useUtm from '@hooks/useUtm'
import useLoading from '@hooks/useLoading'
import useContact from '@hooks/useContact'
import useMagicLink from '@hooks/useMagicLink'
import useInternationalization from '@hooks/useInternationalization'
import useCalculateDiscount from '@hooks/useCalculateDiscount'
import useHasFork from '@hooks/useHasFork'
import useQuery from '@hooks/useQuery'

import getQueryParams from '@client/utils/getQueryParams'
import formatPhone from '@client/utils/formatPhone'

import { createContact } from '@store/ducks/simulate'
import { getCookie } from '@client/services/cookies'

import { openChat } from '@client/utils/chatbot'

import Footer from '@components/Footer'
import YelpPixel from '@components/YelpPixel'

import formSubmitedEvent from '@client/utils/events/formSubmit'

import sendFunnelSubmitEvent from '@client/utils/events/funnelSubmit'
import * as Sections from './sections'

export default function CompanyInfo({ path, actions, step, onLoad, onNext }) {
  const { setFunnelStage, setDataLayer, redirect } = actions

  const { store } = useContext(ReactReduxContext)

  const handleQueryParams = useQuery()
  const { hasMagicLink } = useMagicLink()
  const { applyUtm, utmQueryParams } = useUtm()
  const { contact } = useContact()
  const { startLoad, finishLoad } = useLoading()
  const { country, countryFullName } = useInternationalization()
  const { fork } = useHasFork('smb-portal.fork-new-flow-us')
  const dispatch = useDispatch()

  const employeesNumberThresholdForLargeCompanies = 500
  const funnelStage = 'company_info'

  useCalculateDiscount(contact, setFunnelStage, funnelStage)

  useEffect(() => {
    if (onLoad && !hasMagicLink) {
      const data = {
        id: contact.id,
        funnelStage,
      }
      onLoad(data)
    }
  }, [])

  useEffect(() => {
    if (contact.id) {
      applyUtm(contact.id)
    }
  }, [contact.id])

  useEffect(() => {
    if (hasMagicLink && contact.id) {
      const data = {
        id: contact.id,
        funnelStage,
      }
      onLoad(data)
    }
  }, [hasMagicLink])

  useEffect(() => {
    openChat(30000)
  }, [])

  function goToNextRoute() {
    const { simulate: updatedContact } = store.getState()
    if (updatedContact.emailCheck) {
      if (onNext) onNext()
    } else {
      redirect('confirm-email')
    }
  }

  async function handleForm(formValues) {
    startLoad()

    formSubmitedEvent(country)

    const data = {
      ...formValues,
      companyName: formValues.company,
      country: countryFullName,
      funnelStage,
    }

    const commonValues = {
      'first-name': data.firstName,
      'last-name': data.lastName,
      'work-email': data.email,
      'employees-number': data.employeesNumber,
    }

    const queryParams = {
      ...commonValues,
      phone: formatPhone(country, data.mobilePhone),
      'company-name': data.companyName,
      role: data.role,
      state: data.state,
      coupon: handleQueryParams.get('coupon'),
      ...utmQueryParams,
    }

    const hubspotutk = getCookie('hubspotutk')

    sendFunnelSubmitEvent(country, commonValues, utmQueryParams, data.country)

    if (!contact.id) {
      data.token = hubspotutk

      try {
        await dispatch(createContact(data))
      } catch (error) {
        if (error.response.status === 409) {
          redirect('thank-you/already-eligible')
        }
      }
    }

    if (
      formValues.employeesNumber > employeesNumberThresholdForLargeCompanies
    ) {
      delete data.employeesNumber
      await setFunnelStage(data)

      redirect('thank-you/large-companies')

      return
    }

    await setFunnelStage(data)

    const url = `${env.newFlowForkUrl}/${country}/quote?${getQueryParams(
      queryParams
    )}`

    if (
      fork.isEnabled &&
      formValues.employeesNumber <= fork.employeesThreshold
    ) {
      window.location.replace(url)
      return
    }

    setDataLayer(step, path, {
      actorData: {
        email: formValues.email,
        role: formValues.role,
        department: 'Human Resources',
      },
      companyData: {
        employeesNumber: formValues.employeesNumber,
        industry: formValues.industry,
        state: formValues.state,
      },
    })

    finishLoad()
    goToNextRoute()
  }

  return (
    <>
      <Sections.Appbar />
      <Sections.Main handleForm={handleForm} setFunnelStage={setFunnelStage} />
      <Footer company="Gympass B.V." />
      {contact && contact.id && contact.proposalValue && (
        <YelpPixel
          revenue={contact.proposalValue}
          orderid={contact.id}
          country={country}
        />
      )}
    </>
  )
}

CompanyInfo.propTypes = {
  path: string.isRequired,
  actions: shape({
    setFunnelStage: func,
    setDataLayer: func,
  }).isRequired,
  step: number.isRequired,
  onLoad: func,
  onNext: func,
}

CompanyInfo.defaultProps = {
  onLoad: undefined,
  onNext: undefined,
}
